import "./styles/app.scss";
import "react-simple-keyboard/build/css/index.css";
import "simplebar/dist/simplebar.css";

import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { v4 as uuidv4 } from "uuid";

import FooterBreadcrumbs from "./components/FooterBreadcrumbs";
import FooterNav from "./components/FooterNav";
import FooterShare from "./components/FooterShare";
import KioskModeRestartFooter from "./components/KioskModeRestartFooter";
import { ApiContext, NavigationContext, UserContext } from "./contexts";
import { IS_DEV } from "./resources/constants";
import ChooseExteriorStyleScreen from "./screens/ChooseExteriorStyleScreen";
import ChooseFloorplanScreen from "./screens/ChooseFloorplanScreen";
import ChooseInteriorStyleScreen from "./screens/ChooseInteriorStyleScreen";
import ChooseStreetAppealScreen_v2 from "./screens/ChooseStreetAppealScreen_v2";
import HomeScreen from "./screens/HomeScreen";
import HouseLandDetailsScreen from "./screens/HouseLandDetailsScreen";
import LifestyleOptionsScreen from "./screens/LifestyleOptionsScreen";
import LoginScreen from "./screens/LoginScreen";
import NswFinalDetailsScreen from "./screens/NswFinalDetailsScreen";
import PackageSearchResultsScreen from "./screens/PackageSearchResultsScreen";
import PackageSearchScreen from "./screens/PackageSearchScreen";
import PersonaliseHomeScreen from "./screens/PersonaliseHomeScreen";
import PromotionsScreen from "./screens/PromotionsScreen";
import SignUpScreen from "./screens/SignUpScreen";
import SimpleDetailsScreen from "./screens/SimpleDetailsScreen";
import SpecialOffersScreen from "./screens/SpecialOffersScreen";
import ThankYouScreen from "./screens/ThankYouScreen";
import TurnKeyInclusionsScreen from "./screens/TurnKeyInclusionsScreen";
import UpgradeOptionsScreen from "./screens/UpgradeOptionsScreen";
import YourLandScreen from "./screens/YourLandScreen";
import YourNewHomeScreen from "./screens/YourNewHomeScreen";
import {
  Consultant,
  ExteriorStyle,
  Home,
  HouseType,
  InteriorStyle,
  Region,
  RoutePath,
  SessionState,
  User,
} from "./types";

// @ts-ignore
const beforeUnloadListener = (event) => {
  event.preventDefault();
  return (event.returnValue = "Are you sure you want to exit?");
};

// @ts-ignore
let idelRestartTimerRef;
const idleRestart = (restartFunc: () => void, durationMs: number) => {
  window.onload = resetTimer;
  window.onmousemove = resetTimer;
  window.onmousedown = resetTimer; // catches touchscreen presses as well
  window.ontouchstart = resetTimer; // catches touchscreen swipes as well
  window.ontouchmove = resetTimer; // required by some devices
  window.onclick = resetTimer; // catches touchpad clicks as well
  window.onkeydown = resetTimer;
  window.addEventListener("scroll", resetTimer, true);

  function idleTimeout() {
    restartFunc();
    resetTimer();
  }

  function resetTimer() {
    // @ts-ignore
    clearTimeout(idelRestartTimerRef);
    idelRestartTimerRef = setTimeout(idleTimeout, durationMs);
  }
};

const App = observer(
  (props: {
    settings: any;
    regions: Region[];
    nswRegions: Region[];
    consultants: Consultant[];
    exteriorStyles: ExteriorStyle[];
    interiorStyles: InteriorStyle[];
    home?: Home;
    houseType?: HouseType;
    floorplanOptions?: number[];
    isFlipped?: boolean;
    isKioskMode?: boolean;
    hasKioskMenu?: boolean;
    uuid?: string;
    user?: User;
    sessionState?: SessionState;
  }) => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const navigate = useNavigate();
    const navContext = useContext(NavigationContext);
    const apiContext = useContext(ApiContext);
    const userContext = useContext(UserContext);
    const [isFooterFixed, setIsFooterFixed] = useState(true);

    const animationFrameRef = useRef<number>();

    const onAnimationFrame = () => {
      const app = document.querySelector(".pyht-app");
      const footer = document.querySelector(".pyht-footer");
      if (
        app &&
        footer &&
        app.getBoundingClientRect().bottom > window.innerHeight
      ) {
        setIsFooterFixed(true);
      } else if (!props.isKioskMode) {
        setIsFooterFixed(false);
      }
      animationFrameRef.current = requestAnimationFrame(onAnimationFrame);
    };

    useEffect(() => {
      navContext.setNavigationFunc(navigate);

      if(location.pathname !== RoutePath.CHOOSE_FLOOR_PLAN && location.pathname !== RoutePath.SIMPLE_DETAILS){
        navigate(RoutePath.CHOOSE_FLOOR_PLAN, { replace: true });
      }

      if (props.uuid) {
        userContext.setUUID(props.uuid);
      } else {
        userContext.setUUID(uuidv4());
      }

      if (props.user) {
        userContext.setEmail(props.user.email);
        userContext.setFirstName(props.user.name);
        userContext.setLastName(props.user.surname);
        userContext.setPhone(props.user.phone);
        userContext.setEmail(props.user.email);
        apiContext.setRegion(props.user.region ? props.user.region : null);
        userContext.setEmailSecondary(props.user.email_secondary ? props.user.email_secondary : "");
        userContext.setLandSituation(props.user.land_situation ? props.user.land_situation : "");
        userContext.setLeadStatus(props.user.lead_status ? props.user.lead_status : "");
        userContext.setConsultant(props.user.consultant ? props.user.consultant : null);
        userContext.setIsLoggedIn(true);
      }

      if (props.houseType) {
        apiContext.setHouseType(props.houseType);
      }

      userContext.setIsKioskMode(!!props.isKioskMode);
      userContext.setHasKioskMenu(!!props.hasKioskMenu);

      apiContext.setInitialAppData(
        props.settings,
        props.regions,
        props.nswRegions,
        props.consultants,
        props.exteriorStyles,
        props.interiorStyles
      );

      // If the user came to the app via a Home on the website frontend, setup Mobx with
      // passed in state and send to the last screen they were on
      if (props.home && !props.sessionState) {
        apiContext.setEnteredViaHome(props.home);

        if (props.floorplanOptions) {
          const floorplanOptions = props.floorplanOptions.map((i) =>
            i.toString(10)
          );
          const selectedOptions = [];
          for (const option of props.home.floorplan_options) {
            if (floorplanOptions.indexOf(option.svg_id) > -1) {
              selectedOptions.push(option);
            }
          }

          userContext.setSelectedFloorplanOptions(
            props.home.id,
            selectedOptions
          );
        }

        if (props.isFlipped !== undefined) {
          userContext.setIsFloorplanFlipped(props.home.id, props.isFlipped);
        }
      }

      if (props.sessionState) {
        if (props.home) {
          userContext.restoreSessionState(props.home, props.sessionState);
        }
        navigate(props.sessionState.current_route, { replace: true });
      }
      if (props.isKioskMode) {
        idleRestart(() => {
          navContext.goTo(RoutePath.CHOOSE_FLOOR_PLAN);
          userContext.reset();
          apiContext.reset();
        }, 5 * 60 * 1000);
      }
    }, []);

    useEffect(() => {
      animationFrameRef.current = requestAnimationFrame(onAnimationFrame);
      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (!IS_DEV) {
        try {
          // @ts-ignore
          window.gtag("set", "page_path", window.location.pathname + (userContext.isLoggedIn && !userContext.isNewUser ? "#existinguser" : "#newuser"));
          // @ts-ignore
          window.gtag("event", "page_view");
          // @ts-ignore
          window.ttq.track("ViewContent", {
            "contents": [
              {
                "content_id": window.location.href,
                "content_type": "Page View",
                "content_name": document.title,
              }
            ]
          });
        } catch (e) {
          console.error(e);
        }
      }

      const previousRoute = userContext.currentRoute;
      userContext.setPreviousRoute(previousRoute);
      userContext.setCurrentRoute(location.pathname as RoutePath);

      navContext.setPreviousRoute(previousRoute);
      navContext.setCurrentRoute(location.pathname as RoutePath);

      if (userContext.canSaveAppState && userContext.uuid) {
        apiContext.saveAppState(userContext.uuid, userContext.serialize());
      }

      if (!props.isKioskMode) {
        if (location.pathname === RoutePath.HOME || location.pathname === RoutePath.CHOOSE_FLOOR_PLAN) {
          removeEventListener("beforeunload", beforeUnloadListener, {
            capture: true,
          });
        } else {
          addEventListener("beforeunload", beforeUnloadListener, {
            capture: true,
          });
        }
      }
    }, [IS_DEV, location.pathname, props.isKioskMode]);

    const getSlug = () => {
      if (location.pathname === "/") {
        return "home";
      } else {
        return location.pathname
          .replace("/", "") // Remove leading slash
          .replace(/\//g, "-"); // Replace the rest with dashes
      }
    };

    // Clicking 'restart' uses the replace method, but we need the nav transition to be pop
    let navDirection = navigationType.toLowerCase();
    // @ts-ignore
    if (location.state && location.state.direction) {
      // @ts-ignore
      navDirection = location.state.direction;
    } else if (
      navDirection === "replace" && (
        location.pathname === RoutePath.HOME || 
        location.pathname === RoutePath.CHOOSE_FLOOR_PLAN
      )
    ) {
      navDirection = "pop";
    }

    return (
      <div
        className={`pyht-app ${props.isKioskMode ? "kiosk-mode" : "web-mode"} ${props.hasKioskMenu ? "has-kiosk-menu" : ""}`}
      >
        <div
          className={`route-container route-${getSlug()} ${
            isFooterFixed ? "footer-fixed" : ""
          }`}
        >
          <TransitionGroup
            className={`screen-container nav-slide-${navDirection}`}
          >
            <CSSTransition
              key={location.key}
              classNames={"nav-slide"}
              timeout={600}
            >
              <Routes location={location}>
                <Route path={RoutePath.LOGIN} element={<LoginScreen />} />
                {/* Vacant Block Journey */}
                <Route
                  path={RoutePath.YOUR_LAND}
                  element={<YourLandScreen />}
                />
                <Route
                  path={RoutePath.SIMPLE_DETAILS}
                  element={<SimpleDetailsScreen />}
                />
                <Route
                  path={RoutePath.CHOOSE_FLOOR_PLAN}
                  element={<ChooseFloorplanScreen />}
                />
                <Route
                  path={RoutePath.CHOOSE_STREET_APPEAL}
                  element={<ChooseStreetAppealScreen_v2 />}
                />

                {/* House & Land Journey */}
                <Route
                  path={RoutePath.PACKAGE_SEARCH}
                  element={<PackageSearchScreen />}
                />
                <Route
                  path={RoutePath.PACKAGE_SEARCH_RESULTS}
                  element={<PackageSearchResultsScreen />}
                />

                <Route
                  path={RoutePath.PACKAGE_DETAILS}
                  element={<HouseLandDetailsScreen />}
                />

                {/* Screens shared by all house type journeys */}
                <Route
                  path={RoutePath.CHOOSE_EXTERIOR_STYLE}
                  element={<ChooseExteriorStyleScreen />}
                />

                <Route
                  path={RoutePath.PROMOTIONS}
                  element={<PromotionsScreen />}
                />

                <Route
                  path={RoutePath.PERSONALISE_HOME}
                  element={<PersonaliseHomeScreen />}
                />

                <Route path={RoutePath.SIGN_UP} element={<SignUpScreen />} />

                {/* NSW ONLY ROUTE */}
                <Route
                  path={RoutePath.UPGRADE_OPTIONS}
                  element={<UpgradeOptionsScreen />}
                />

                {/* QLD ONLY ROUTE */}
                <Route
                  path={RoutePath.TURN_KEY_INCLUSIONS}
                  element={<TurnKeyInclusionsScreen />}
                />

                <Route
                  path={RoutePath.INTERIOR_STYLE}
                  element={<ChooseInteriorStyleScreen />}
                />

                <Route
                  path={RoutePath.LIFESTYLE_OPTIONS}
                  element={<LifestyleOptionsScreen />}
                />

                <Route
                  path={RoutePath.SPECIAL_OFFERS}
                  element={<SpecialOffersScreen />}
                />

                <Route
                  path={RoutePath.FINAL_DETAILS}
                  element={<NswFinalDetailsScreen />}
                />

                <Route
                  path={RoutePath.YOUR_NEW_HOME}
                  element={<YourNewHomeScreen />}
                />

                <Route
                  path={RoutePath.THANK_YOU}
                  element={<ThankYouScreen />}
                />

                <Route path={RoutePath.HOME} element={<HomeScreen />} />
              </Routes>
            </CSSTransition>
          </TransitionGroup>
          <footer
            className={`pyht-footer state-nsw ${isFooterFixed ? "fixed" : ""} ${props.isKioskMode ? "kiosk-mode" : ""}`}
          >
            {location.pathname === RoutePath.YOUR_NEW_HOME &&
              !props.isKioskMode && <FooterShare />}

            {props.isKioskMode && [RoutePath.YOUR_NEW_HOME, RoutePath.FINAL_DETAILS, RoutePath.THANK_YOU].includes(navContext.currentRoute) ? (
              <KioskModeRestartFooter />
            ) : (
              <FooterNav />
            )}

            <FooterBreadcrumbs />
          </footer>
        </div>
      </div>
    );
  }
);

export default App;
